class HeyBooking extends HTMLElement {
  connectedCallback() {
    this.locale = 'tr-TR';
    this.data = this.readData();
    this.timeZone = this.data.timeZone || 'Europe/Berlin';
    this.customerTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || this.timeZone;
    this.availabilityCache = new Map();
    this.availabilityLoading = false;
    this.availabilityError = '';
    this.services = this.normalizeServices(this.data.services || []);
    const packageLink = this.readPackageLink();
    if (packageLink.code) this.rememberPackageLink(packageLink.code);
    this.prefilledPackageCode = packageLink.code || this.resumePackageLink();
    this.selectedService = (this.prefilledPackageCode
      ? this.services.find((service) => service.id === 'individual')
      : null) || this.services[0] || null;
    this.selectedDate = null;
    this.selectedSlot = null;
    this.selectedMethod = null;
    this.visibleMonth = this.firstAvailableMonth();
    this.bindElements();
    this.ensureTimezoneRows();
    this.bindEvents();
    this.render();
    if (this.isManagedAppointment()) void this.loadManagedAvailability();
    if (packageLink.seen) window.location.replace('#randevu-takvimi');
  }

  readPackageLink() {
    const prefix = '#paket-kodu=';
    if (!window.location.hash.startsWith(prefix)) return { seen: false, code: '' };
    try {
      const code = decodeURIComponent(window.location.hash.slice(prefix.length)).trim().toUpperCase();
      return {
        seen: true,
        code: /^HEY-(3|6)-[A-HJ-NP-Z2-9]{4}(?:-[A-HJ-NP-Z2-9]{4}){2}$/.test(code) ? code : '',
      };
    } catch (_error) {
      return { seen: true, code: '' };
    }
  }

  rememberPackageLink(code) {
    try {
      window.sessionStorage.setItem('hey_package_link_pending', JSON.stringify({ code, expiresAt: Date.now() + 30000 }));
    } catch (_error) {
      // The link still works on browsers that block session storage.
    }
  }

  resumePackageLink() {
    if (window.location.hash !== '#randevu-takvimi') return '';
    try {
      const raw = window.sessionStorage.getItem('hey_package_link_pending');
      window.sessionStorage.removeItem('hey_package_link_pending');
      const pending = JSON.parse(raw || 'null');
      const code = String(pending?.code || '');
      return pending?.expiresAt > Date.now() && /^HEY-(3|6)-[A-HJ-NP-Z2-9]{4}(?:-[A-HJ-NP-Z2-9]{4}){2}$/.test(code)
        ? code
        : '';
    } catch (_error) {
      return '';
    }
  }

  readData() {
    const node = this.querySelector('[data-booking-data]');
    if (!node) return { services: [] };
    try { return JSON.parse(node.textContent); } catch (_error) { return { services: [] }; }
  }

  normalizeServices(services) {
    const earliestDate = this.dateISOAfterDays(Number(this.data.minAdvanceDays ?? 1));
    return services.map((service) => {
      if (service.kind === 'managed-appointment') {
        if (!service.checkoutVariantId || !this.data.availabilityApiUrl) return null;
        return { ...service, slots: [] };
      }
      const slots = (service.variants || []).map((variant) => {
        const match = String(variant.title || '').match(/(\d{4}-\d{2}-\d{2}).*?(\d{2}:\d{2})/);
        if (!match || variant.available === false || match[1] < earliestDate) return null;
        return this.createDisplaySlot({
          id: String(variant.id),
          providerDate: match[1],
          providerTime: match[2],
          price: variant.price || service.price,
        });
      }).filter(Boolean);
      return { ...service, slots };
    }).filter((service) => service && (service.kind === 'managed-appointment' || service.slots.length));
  }

  bindElements() {
    this.serviceList = this.querySelector('[data-services]');
    this.monthLabel = this.querySelector('[data-month-label]');
    this.days = this.querySelector('[data-days]');
    this.times = this.querySelector('[data-times]');
    this.prevButton = this.querySelector('[data-prev-month]');
    this.nextButton = this.querySelector('[data-next-month]');
    this.summaryService = this.querySelector('[data-summary-service]');
    this.summaryDate = this.querySelector('[data-summary-date]');
    this.summaryTime = this.querySelector('[data-summary-time]');
    this.summaryPrice = this.querySelector('[data-summary-price]');
    this.summaryMethod = this.querySelector('[data-summary-method]');
    this.summaryPhone = this.querySelector('[data-summary-phone]');
    this.whatsAppPhone = this.querySelector('[data-whatsapp-phone]');
    this.summaryProviderTime = this.querySelector('[data-summary-provider-time]');
    this.summaryProviderRow = this.summaryProviderTime?.closest('.hey-booking__summary-row');
    this.continueButton = this.querySelector('[data-continue]');
    this.privacyConsent = this.querySelector('[data-privacy-consent]');
    this.termsConsent = this.querySelector('[data-terms-consent]');
    this.termsConsentRow = this.querySelector('[data-terms-consent-row]');
    this.discountCode = this.querySelector('[data-discount-code]');
    this.discountCodeRow = this.querySelector('[data-discount-code-row]');
    this.checkoutStatus = this.querySelector('[data-checkout-status]');
  }

  bindEvents() {
    this.prevButton?.addEventListener('click', () => this.changeMonth(-1));
    this.nextButton?.addEventListener('click', () => this.changeMonth(1));
    this.querySelectorAll('[data-method]').forEach((button) => button.addEventListener('click', () => {
      this.selectedMethod = button.dataset.method;
      this.renderMethods();
      this.renderSummary();
    }));
    this.privacyConsent?.addEventListener('change', () => this.renderSummary());
    this.termsConsent?.addEventListener('change', () => this.renderSummary());
    this.whatsAppPhone?.addEventListener('input', () => this.renderSummary());
    this.continueButton?.addEventListener('click', () => this.startCheckout());
  }

  normalizedWhatsAppPhone() {
    return String(this.whatsAppPhone?.value || '').trim().replace(/\s+/g, ' ');
  }

  hasValidWhatsAppPhone() {
    const value = this.normalizedWhatsAppPhone();
    const digits = value.replace(/\D/g, '');
    return value.startsWith('+') && digits.length >= 9 && digits.length <= 15;
  }

  normalizedDiscountCode() {
    return String(this.discountCode?.value || '').trim();
  }

  checkoutDestination() {
    const root = window.Shopify?.routes?.root || '/';
    const checkoutPath = `${root}checkout`;
    const code = this.selectedService?.id === 'individual' && this.prefilledPackageCode
      ? this.prefilledPackageCode
      : this.selectedService?.id === 'intro' ? '' : this.normalizedDiscountCode();
    if (!code) return checkoutPath;
    return `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(checkoutPath)}`;
  }

  ensureTimezoneRows() {
    const row = this.querySelector('[data-summary-timezone]');
    const description = row?.querySelector('dd');
    if (description) {
      description.textContent = `Saatler cihazınızın saat dilimine göre gösterilir: ${this.customerTimeZone}`;
    }
  }

  isManagedAppointment(service = this.selectedService) {
    return service?.kind === 'managed-appointment';
  }

  firstAvailableMonth() {
    const service = this.services[0];
    return this.firstServiceMonth(service);
  }

  firstServiceMonth(service = this.selectedService) {
    let dateValue;
    if (this.isManagedAppointment(service)) {
      dateValue = this.firstManagedSlot(service)?.date;
    } else {
      dateValue = [...new Set((service?.slots || []).map((slot) => slot.date))].sort()[0];
    }
    const date = dateValue ? this.parseDate(dateValue) : new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  managedProviderTimes(service = this.selectedService) {
    if (service?.id === 'intro') return ['14:00', '14:30'];
    return Array.from({ length: 11 }, (_value, index) => (
      `${String(9 + index).padStart(2, '0')}:00`
    ));
  }

  firstManagedSlot(service = this.selectedService) {
    let providerDate = this.todayISO();
    for (let offset = 0; offset < 8; offset += 1) {
      const candidate = this.addDateDays(providerDate, offset);
      if (this.isWeekday(candidate)) {
        const providerTime = this.managedProviderTimes(service)[0];
        return this.createDisplaySlot({
          id: `${candidate}T${providerTime}`,
          providerDate: candidate,
          providerTime,
        });
      }
    }
    return null;
  }

  parseDate(value) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  todayISO() {
    return this.datePartsInZone(new Date(), this.timeZone).date;
  }

  dateISOAfterDays(days) {
    return this.addDateDays(this.todayISO(), Math.max(0, days));
  }

  addDateDays(value, days) {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day + days));
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
  }

  isWeekday(value) {
    const [year, month, day] = value.split('-').map(Number);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return weekday >= 1 && weekday <= 5;
  }

  zonedDateTimeToUtc(dateValue, timeValue, timeZone) {
    const [year, month, day] = dateValue.split('-').map(Number);
    const [hour, minute] = timeValue.split(':').map(Number);
    const target = Date.UTC(year, month - 1, day, hour, minute);
    let guess = target;
    for (let iteration = 0; iteration < 3; iteration += 1) {
      const parts = this.datePartsInZone(new Date(guess), timeZone);
      const represented = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
      guess += target - represented;
    }
    return new Date(guess);
  }

  datePartsInZone(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
      hour: Number(values.hour),
      minute: Number(values.minute),
      date: `${values.year}-${values.month}-${values.day}`,
      time: `${values.hour}:${values.minute}`,
    };
  }

  createDisplaySlot({ id, providerDate, providerTime, price }) {
    const instant = this.zonedDateTimeToUtc(providerDate, providerTime, this.timeZone);
    const local = this.datePartsInZone(instant, this.customerTimeZone);
    return {
      id: String(id),
      providerDate,
      providerTime,
      date: local.date,
      time: local.time,
      instant: instant.toISOString(),
      price,
    };
  }

  managedSlotsForMonth(month = this.visibleMonth) {
    if (!month) return [];
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const localStart = new Date(Date.UTC(year, monthIndex, -1));
    const localEnd = new Date(Date.UTC(year, monthIndex + 1, 2));
    const earliest = this.todayISO();
    const slots = [];
    for (let cursor = new Date(localStart); cursor <= localEnd; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
      const providerDate = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}-${String(cursor.getUTCDate()).padStart(2, '0')}`;
      if (providerDate < earliest || !this.isWeekday(providerDate)) continue;
      for (const providerTime of this.managedProviderTimes()) {
        const slot = this.createDisplaySlot({
          id: `${providerDate}T${providerTime}`,
          providerDate,
          providerTime,
        });
        const [localYear, localMonth] = slot.date.split('-').map(Number);
        if (localYear === year && localMonth === monthIndex + 1) slots.push(slot);
      }
    }
    return slots;
  }

  currentSlots() {
    return this.isManagedAppointment() ? this.managedSlotsForMonth() : (this.selectedService?.slots || []);
  }

  serviceDates() {
    const unavailable = this.currentUnavailableSlots();
    const slots = this.currentSlots();
    const dates = new Set();
    slots.forEach((slot) => {
      if (!this.isManagedAppointment() || !unavailable.has(slot.id)) dates.add(slot.date);
    });
    return [...dates].sort();
  }

  availabilityKey(month = this.visibleMonth) {
    return `${this.selectedService?.id || 'appointment'}:${this.monthKey(month)}`;
  }

  currentUnavailableSlots() {
    return this.availabilityCache.get(this.availabilityKey()) || new Set();
  }

  async loadManagedAvailability(force = false) {
    if (!this.isManagedAppointment()) return;
    const key = this.availabilityKey();
    if (!force && this.availabilityCache.has(key)) return;
    const slots = this.managedSlotsForMonth();
    const providerDates = [...new Set(slots.map((slot) => slot.providerDate))].sort();
    if (!providerDates.length) {
      this.availabilityCache.set(key, new Set());
      this.render();
      return;
    }

    this.availabilityLoading = true;
    this.availabilityError = '';
    this.renderCalendar();
    this.renderTimes();
    try {
      const base = String(this.data.availabilityApiUrl || '').replace(/\/$/, '');
      const query = new URLSearchParams({
        from: providerDates[0],
        to: providerDates.at(-1),
        serviceType: this.selectedService.id,
      });
      const response = await fetch(`${base}/api/availability?${query}`, { headers: { Accept: 'application/json' } });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Müsaitlik alınamadı.');
      this.availabilityCache.set(key, new Set(payload.unavailable || []));
    } catch (error) {
      this.availabilityError = error.message || 'Müsaitlik şu anda kontrol edilemiyor.';
    } finally {
      this.availabilityLoading = false;
      this.renderCalendar();
      this.renderTimes();
      this.renderSummary();
    }
  }

  escapeHTML(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
    })[character]);
  }

  formatDate(value) {
    const [year, month, day] = value.split('-').map(Number);
    return new Intl.DateTimeFormat(this.locale, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, day)));
  }

  formatProviderSlot(slot) {
    if (!slot) return 'Henüz seçilmedi';
    return `${this.formatDate(slot.providerDate)} · ${slot.providerTime}`;
  }

  render() {
    this.renderServices();
    this.renderCalendar();
    this.renderTimes();
    this.renderMethods();
    this.renderSummary();
  }

  renderServices() {
    if (!this.serviceList) return;
    if (!this.services.length) {
      this.serviceList.innerHTML = '<p class="hey-booking__empty">Randevu ürünleri henüz bağlanmadı. Lütfen daha sonra tekrar deneyin.</p>';
      return;
    }
    this.serviceList.innerHTML = this.services.map((service) => `
      <button class="hey-booking__service" type="button" data-service="${service.id}" aria-pressed="${service.id === this.selectedService?.id}">
        <span class="hey-booking__service-title">${this.escapeHTML(service.title)}</span>
        <span class="hey-booking__service-meta">${this.escapeHTML(service.meta || '')}</span>
      </button>`).join('');
    this.serviceList.querySelectorAll('[data-service]').forEach((button) => button.addEventListener('click', () => {
      this.selectedService = this.services.find((service) => service.id === button.dataset.service);
      this.selectedDate = null;
      this.selectedSlot = null;
      if (this.privacyConsent) this.privacyConsent.checked = false;
      if (this.termsConsent) this.termsConsent.checked = false;
      this.visibleMonth = this.firstServiceMonth();
      this.availabilityError = '';
      this.render();
      if (this.isManagedAppointment()) void this.loadManagedAvailability();
    }));
  }

  renderCalendar() {
    if (!this.days || !this.monthLabel || !this.visibleMonth) return;
    this.monthLabel.textContent = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(this.visibleMonth);
    const year = this.visibleMonth.getFullYear();
    const month = this.visibleMonth.getMonth();
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7;
    const count = new Date(year, month + 1, 0).getDate();
    const available = new Set(this.serviceDates());
    const cells = [];
    for (let index = 0; index < offset; index += 1) cells.push('<span class="hey-booking__day" aria-hidden="true"></span>');
    for (let day = 1; day <= count; day += 1) {
      const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push(available.has(date)
        ? `<button class="hey-booking__day" type="button" data-date="${date}" aria-label="${this.formatDate(date)}" aria-pressed="${date === this.selectedDate}" ${this.availabilityLoading ? 'disabled' : ''}>${day}</button>`
        : `<span class="hey-booking__day">${day}</span>`);
    }
    this.days.innerHTML = cells.join('');
    this.days.querySelectorAll('[data-date]').forEach((button) => button.addEventListener('click', () => {
      this.selectedDate = button.dataset.date;
      this.selectedSlot = null;
      this.renderCalendar();
      this.renderTimes();
      this.renderSummary();
    }));
    if (this.isManagedAppointment()) {
      const firstMonth = this.firstServiceMonth();
      this.prevButton.disabled = this.monthKey(this.visibleMonth) <= this.monthKey(firstMonth) || this.availabilityLoading;
      this.nextButton.disabled = this.availabilityLoading;
    } else {
      const months = this.availableMonths();
      const position = months.indexOf(this.monthKey(this.visibleMonth));
      this.prevButton.disabled = position <= 0;
      this.nextButton.disabled = position === -1 || position >= months.length - 1;
    }
  }

  availableMonths() {
    return [...new Set((this.selectedService?.slots || []).map((slot) => slot.date.slice(0, 7)))].sort();
  }

  monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  changeMonth(direction) {
    if (this.isManagedAppointment()) {
      const target = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() + direction, 1);
      if (this.monthKey(target) < this.monthKey(this.firstServiceMonth())) return;
      this.visibleMonth = target;
      this.selectedDate = null;
      this.selectedSlot = null;
      this.render();
      void this.loadManagedAvailability();
      return;
    }
    const months = this.availableMonths();
    const current = months.indexOf(this.monthKey(this.visibleMonth));
    const target = months[current + direction];
    if (!target) return;
    const [year, month] = target.split('-').map(Number);
    this.visibleMonth = new Date(year, month - 1, 1);
    this.renderCalendar();
  }

  renderTimes() {
    if (!this.times) return;
    if (this.availabilityLoading && this.isManagedAppointment()) {
      this.times.innerHTML = '<p class="hey-booking__empty">Uygun saatler kontrol ediliyor…</p>';
      return;
    }
    if (this.availabilityError && this.isManagedAppointment()) {
      this.times.innerHTML = `<p class="hey-booking__empty">${this.escapeHTML(this.availabilityError)} Lütfen kısa bir süre sonra tekrar deneyin.</p>`;
      return;
    }
    if (!this.selectedDate) {
      this.times.innerHTML = '<p class="hey-booking__empty">Uygun saatleri görmek için takvimden bir gün seçin.</p>';
      return;
    }
    const unavailable = this.currentUnavailableSlots();
    const slots = this.currentSlots()
      .filter((slot) => slot.date === this.selectedDate && (!this.isManagedAppointment() || !unavailable.has(slot.id)))
      .sort((a, b) => a.instant.localeCompare(b.instant));
    if (!slots.length) {
      this.times.innerHTML = '<p class="hey-booking__empty">Bu gün için uygun saat kalmadı. Lütfen başka bir gün seçin.</p>';
      return;
    }
    this.times.innerHTML = slots.map((slot) => `<button class="hey-booking__time" type="button" data-slot="${slot.id}" aria-pressed="${slot.id === this.selectedSlot?.id}">${slot.time}</button>`).join('');
    this.times.querySelectorAll('[data-slot]').forEach((button) => button.addEventListener('click', () => {
      this.selectedSlot = slots.find((slot) => slot.id === button.dataset.slot);
      this.renderTimes();
      this.renderSummary();
    }));
  }

  renderMethods() {
    this.querySelectorAll('[data-method]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.method === this.selectedMethod));
    });
  }

  async apiRequest(path, options = {}) {
    const base = String(this.data.availabilityApiUrl || '').replace(/\/$/, '');
    const response = await fetch(`${base}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(payload.error || 'Randevu işlemi tamamlanamadı.');
      error.status = response.status;
      throw error;
    }
    return payload;
  }

  async createReservation() {
    return this.apiRequest('/api/reservations', {
      method: 'POST',
      body: JSON.stringify({
        slotKey: this.selectedSlot.id,
        method: this.selectedMethod,
        serviceType: this.selectedService.id,
      }),
    });
  }

  async releaseReservation(token) {
    if (!token || !this.data.availabilityApiUrl) return;
    try {
      await this.apiRequest('/api/reservations/release', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
    } catch (_error) {
      // Rezervasyon kendiliğinden sona erer; kullanıcı akışı bu yüzden kesilmez.
    }
  }

  async startCheckout() {
    const requiresTermsConsent = this.selectedService?.id !== 'intro';
    if (!this.selectedSlot || !this.selectedMethod || !this.privacyConsent?.checked
      || !this.hasValidWhatsAppPhone()
      || (requiresTermsConsent && !this.termsConsent?.checked)) return;
    if (this.data.demoMode) {
      this.checkoutStatus.className = 'hey-booking__success';
      this.checkoutStatus.textContent = 'Yerel önizleme tamamlandı. Shopify mağazasında bu adım güvenli ödeme sayfasını açacaktır.';
      return;
    }

    this.continueButton.disabled = true;
    this.continueButton.textContent = 'Ödeme hazırlanıyor…';
    this.checkoutStatus.className = '';
    this.checkoutStatus.textContent = '';
    let reservation = null;

    try {
      await this.removeExistingAppointments();
      if (this.isManagedAppointment()) reservation = await this.createReservation();
      const properties = {
        'Randevu Tarihi': this.formatDate(this.selectedSlot.date),
        'Randevu Saati': this.selectedSlot.time,
        'Müşteri Saat Dilimi': this.customerTimeZone,
        'Almanya Tarihi': this.formatDate(this.selectedSlot.providerDate),
        'Almanya Saati': this.selectedSlot.providerTime,
        'Görüşme Şekli': this.selectedMethod,
        'WhatsApp Numarası': this.normalizedWhatsAppPhone(),
        '_hey_privacy_notice_version': '2026-08-23',
        '_hey_special_data_consent_at': new Date().toISOString(),
        '_hey_randevu': 'true',
        '_hey_service_type': this.selectedService.id,
      };
      if (requiresTermsConsent) {
        properties._hey_terms_version = '2026-08-23';
        properties._hey_early_performance_consent_at = new Date().toISOString();
        properties._hey_withdrawal_acknowledged_at = new Date().toISOString();
      }
      if (reservation) {
        properties._hey_booking_token = reservation.token;
        properties._hey_slot_key = reservation.slotKey;
      }
      const variantId = this.isManagedAppointment() ? this.selectedService.checkoutVariantId : this.selectedSlot.id;
      const response = await fetch(this.data.checkoutUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties }] }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Seçtiğiniz randevu artık uygun olmayabilir.');
      }
      try { window.sessionStorage.removeItem('hey_package_link_pending'); } catch (_error) { /* Optional storage. */ }
      window.location.assign(this.checkoutDestination());
    } catch (error) {
      if (reservation?.token) await this.releaseReservation(reservation.token);
      if (error.status === 409 && this.isManagedAppointment()) {
        const unavailable = this.currentUnavailableSlots();
        unavailable.add(this.selectedSlot.id);
        this.availabilityCache.set(this.availabilityKey(), unavailable);
        this.selectedSlot = null;
        this.renderCalendar();
        this.renderTimes();
      }
      this.checkoutStatus.className = 'hey-booking__error';
      this.checkoutStatus.textContent = error.message || 'Randevu ödeme sayfasına aktarılamadı. Lütfen tekrar deneyin.';
      this.continueButton.disabled = false;
      this.continueButton.textContent = 'Shopify ödeme sayfasına geç';
    }
  }

  async removeExistingAppointments() {
    const cartResponse = await fetch(this.data.cartUrl || '/cart.js', { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('Sepet kontrol edilemedi. Lütfen tekrar deneyin.');
    const cart = await cartResponse.json();
    const updates = {};
    const tokens = [];
    (cart.items || []).forEach((item) => {
      const properties = item.properties || {};
      if (properties._hey_randevu === 'true' || (properties['Randevu Tarihi'] && properties['Randevu Saati'])) {
        updates[item.key] = 0;
        if (properties._hey_booking_token) tokens.push(properties._hey_booking_token);
      }
    });
    if (!Object.keys(updates).length) return;
    const updateResponse = await fetch(this.data.cartUpdateUrl || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!updateResponse.ok) throw new Error('Önceki randevu sepetten kaldırılamadı. Lütfen tekrar deneyin.');
    await Promise.all(tokens.map((token) => this.releaseReservation(token)));
  }

  renderSummary() {
    if (!this.selectedService) return;
    this.summaryService.textContent = this.selectedService.title;
    this.summaryDate.textContent = this.selectedDate ? this.formatDate(this.selectedDate) : 'Henüz seçilmedi';
    this.summaryTime.textContent = this.selectedSlot?.time || 'Henüz seçilmedi';
    this.summaryMethod.textContent = this.selectedMethod || 'Henüz seçilmedi';
    if (this.summaryPhone) this.summaryPhone.textContent = this.normalizedWhatsAppPhone() || 'Henüz girilmedi';
    if (this.summaryProviderTime) this.summaryProviderTime.textContent = this.formatProviderSlot(this.selectedSlot);
    if (this.summaryProviderRow) this.summaryProviderRow.hidden = !this.selectedSlot;
    this.summaryPrice.textContent = this.selectedSlot?.priceFormatted || this.selectedService.priceFormatted || 'Randevu adımında';
    const hasConsent = Boolean(this.privacyConsent?.checked);
    const requiresTermsConsent = this.selectedService?.id !== 'intro';
    if (this.discountCodeRow) this.discountCodeRow.hidden = !requiresTermsConsent;
    if (this.termsConsentRow) this.termsConsentRow.hidden = !requiresTermsConsent;
    if (this.termsConsent) {
      this.termsConsent.required = requiresTermsConsent;
      this.termsConsent.disabled = !requiresTermsConsent;
    }
    const hasTermsConsent = !requiresTermsConsent || Boolean(this.termsConsent?.checked);
    const hasWhatsAppPhone = this.hasValidWhatsAppPhone();
    this.continueButton.disabled = !this.selectedSlot || !this.selectedMethod || !hasWhatsAppPhone || !hasConsent || !hasTermsConsent;
    if (!this.selectedSlot) this.continueButton.textContent = 'Tarih ve saat seçin';
    else if (!this.selectedMethod) this.continueButton.textContent = 'Görüşme şeklini seçin';
    else if (!hasWhatsAppPhone) this.continueButton.textContent = 'WhatsApp numaranızı ülke koduyla girin';
    else if (!hasConsent) this.continueButton.textContent = 'Veri koruma onayını işaretleyin';
    else if (!hasTermsConsent) this.continueButton.textContent = 'Hizmet ve cayma koşullarını onaylayın';
    else this.continueButton.textContent = this.data.demoMode ? 'Seçimi önizle' : 'Shopify ödeme sayfasına geç';
  }
}

if (!customElements.get('hey-booking')) customElements.define('hey-booking', HeyBooking);
